import { SATOSPortal } from "@/data-adapter/SATOSPortal";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { LabQueryKeys } from "@/service/LabHomeQueries";

export const useUpdateMeasurementGroup = (id?: number) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (payload) => SATOSPortal.UpdateMeasurementGroup(payload),
		onMutate: async () => {
			// cancel anything that might interfere
			await Promise.all([
				queryClient.cancelQueries({ queryKey: [LabQueryKeys.labDaily] }),
				queryClient.cancelQueries({ queryKey: [LabQueryKeys.labGroups, id] }),
			]);
		},

		onError: (err) => {
			console.error("Mutation failed:", err);
		},

		onSettled: () => {
			// refresh both general + group-specific data
			void queryClient.invalidateQueries({ queryKey: [LabQueryKeys.labDaily] });
			void queryClient.invalidateQueries({
				queryKey: [LabQueryKeys.labGroups, id],
			});
		},
	});
};

export const useSaveSearch = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (payload) => SATOSPortal.SaveSearch(payload),
		onMutate: async () => {
			await queryClient.cancelQueries({ queryKey: [LabQueryKeys.labSearch] });
		},
		onSettled: () => {
			void queryClient.invalidateQueries({
				queryKey: [LabQueryKeys.labSearch],
			});
		},
	});
};

export const useEditLabGroup = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (payload) => SATOSPortal.UpdateLabGroup(payload),
		onMutate: async () => {
			await queryClient.cancelQueries({ queryKey: [LabQueryKeys.labDaily] });
		},

		onSettled: () => {
			void queryClient.invalidateQueries({ queryKey: [LabQueryKeys.labDaily] });
		},
	});
};

export const useEditLabAnalyze = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (payload) => SATOSPortal.UpdateLabAnalyze(payload),
		onMutate: async () => {
			await queryClient.cancelQueries({ queryKey: [LabQueryKeys.labDaily] });
		},
		onSettled: () => {
			void queryClient.invalidateQueries({ queryKey: [LabQueryKeys.labDaily] });
		},
	});
};

export const useUpdateMeasurement = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (payload) => SATOSPortal.UpdateMeasurement(payload),
		onMutate: async () => {
			await queryClient.cancelQueries({ queryKey: [LabQueryKeys.labDaily] });
		},

		onSettled: () => {
			void queryClient.invalidateQueries({ queryKey: [LabQueryKeys.labDaily] });
		},
	});
};

export const useGetLabReports = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (payload) => SATOSPortal.GetLabReports(payload),
		onMutate: async () => {
			await queryClient.cancelQueries({ queryKey: [LabQueryKeys.labDaily] });
		},

		onSettled: () => {
			void queryClient.invalidateQueries({ queryKey: [LabQueryKeys.labDaily] });
		},
	});
};

export const useCreateLabReport = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (payload) => SATOSPortal.PostLabArchive(payload),
		onMutate: async () => {
			await queryClient.cancelQueries({ queryKey: [LabQueryKeys.labDaily] });
		},

		onSettled: () => {
			void queryClient.invalidateQueries({ queryKey: [LabQueryKeys.labDaily] });
		},
	});
};

// TODO: Ustin - Is this implemented correctly?
export const useMutateCreateComment = () => {
	const queryClient = useQueryClient();

	const { mutateAsync: createComment, isPending: isSubmitting } = useMutation({
		mutationFn: (request: Parameters<typeof SATOSPortal.CreateComment>[0]) =>
			SATOSPortal.CreateComment(request),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ["comments"] });
		},
	});

	return { createComment, isSubmitting };
};

export const useDeleteLabMeasurement = () => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: (id: number) => SATOSPortal.DeleteLabMeasurement(id),
		onMutate: async () => {
			await queryClient.cancelQueries({ queryKey: [LabQueryKeys.labDaily] });
		},
		onSettled: () => {
			void queryClient.invalidateQueries({ queryKey: [LabQueryKeys.labDaily] });
		},
	});

	return mutation;
};

export const useDeleteLabGroup = () => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: (id: number) => SATOSPortal.DeleteLabGroup(id),
		onMutate: async () => {
			await queryClient.cancelQueries({ queryKey: [LabQueryKeys.labDaily] });
		},

		onSettled: () => {
			void queryClient.invalidateQueries({ queryKey: [LabQueryKeys.labDaily] });
		},
	});

	return mutation;
};

export const useDeleteLabAnalyze = () => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: (id: number) => SATOSPortal.DeleteLabAnalyze(id),
		onMutate: async () => {
			await queryClient.cancelQueries({ queryKey: [LabQueryKeys.labDaily] });
		},

		onSettled: () => {
			void queryClient.invalidateQueries({ queryKey: [LabQueryKeys.labDaily] });
		},
	});

	return mutation;
};
